import React from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Stepper, Step, StepLabel, StepContent,
} from '@mui/material';
import { KbItemProgression, KbItemStatus, TrainingInsights, scoreColor } from '../types';
import SectionShell from './SectionShell';

interface Props {
  data: TrainingInsights['salesIntelligence'];
}

const STATUS_CHIP: Record<KbItemStatus, { label: string; color: 'default' | 'warning' }> = {
  answered: { label: 'Answered', color: 'default' },
  no_answer: { label: 'No answer', color: 'warning' },
  not_attempted: { label: 'Not attempted', color: 'default' },
};

/**
 * Normalise a record from any analysis generation. Sessions analysed before
 * the read-out mode carry no status (or only 'not_attempted') plus fields
 * this view no longer reads; anything without a known status reads as
 * answered, since those sessions only recorded items the rep engaged with.
 */
export const resolveKbItemStatus = (item: Partial<KbItemProgression>): KbItemStatus =>
  item?.status === 'no_answer' || item?.status === 'not_attempted' ? item.status : 'answered';

const resolveScore = (item: Partial<KbItemProgression>, status: KbItemStatus): number | null => {
  if (status === 'not_attempted') return null;
  return typeof item?.handlingScore === 'number' ? item.handlingScore : null;
};

const ScoreIcon: React.FC<{ score: number | null }> = ({ score }) =>
  score == null ? (
    <Chip label="n/a" size="small" variant="outlined" sx={{ opacity: 0.6 }} />
  ) : (
    <Chip label={`${score}%`} size="small" color={scoreColor(score)} />
  );

const stepDetail = (item: Partial<KbItemProgression>, status: KbItemStatus): string => {
  if (status === 'not_attempted') {
    return 'The session ended before the AI customer reached this item, so no score was assigned.';
  }
  if (item?.oneLineSummary) return item.oneLineSummary;
  return status === 'no_answer' ? 'The rep did not answer.' : '';
};

const KbItemStep: React.FC<{ item: Partial<KbItemProgression> }> = ({ item }) => {
  const status = resolveKbItemStatus(item);
  const isNotAttempted = status === 'not_attempted';
  const detail = stepDetail(item, status);
  return (
    <>
      <StepLabel StepIconProps={{ icon: <ScoreIcon score={resolveScore(item, status)} /> }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ opacity: isNotAttempted ? 0.7 : 1 }}>
          {item?.itemType && <Chip label={item.itemType} size="small" variant="outlined" />}
          <Chip
            label={STATUS_CHIP[status].label}
            size="small"
            color={STATUS_CHIP[status].color}
            variant="outlined"
            sx={isNotAttempted ? { color: 'text.secondary' } : undefined}
          />
          <Typography variant="subtitle2" component="span">
            {item?.question || '(no question text)'}
          </Typography>
        </Box>
      </StepLabel>
      <StepContent>
        {detail && (
          <Typography variant="body2" color="text.secondary">
            {detail}
          </Typography>
        )}
      </StepContent>
    </>
  );
};

const KB_BREAKDOWN_INTRO = 'Each knowledge base item in the order the AI customer read it out, whether the '
  + 'rep answered it, and how the answer scored after the session.';

/**
 * Training-only section. Replaces the "phase view" from post-call adherence
 * (which doesn't fit training: there's no organic opener/discovery/close).
 * Instead shows the knowledge base items in the order the AI customer read
 * them out, whether the rep answered each, and how the analysis scored it.
 */
const KbItemProgressionSection: React.FC<Props> = ({ data }) => {
  if (!data) return null;
  const progression = data.kbItemProgression || [];
  const showEmptyKbState = progression.length === 0;

  return (
    <SectionShell
      title="Knowledge Base Item Breakdown"
      valueLabel={`${progression.length} item(s)`}
      summary={KB_BREAKDOWN_INTRO}
    >

      {showEmptyKbState ? (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No Knowledge Base items were surfaced during this session.
        </Typography>
      ) : (
        <Stepper orientation="vertical" nonLinear activeStep={-1}>
          {progression.map((item, idx) => (
            <Step key={`${item?.trackItemId}-${idx}`} expanded>
              <KbItemStep item={item} />
            </Step>
          ))}
        </Stepper>
      )}

      {/* Buying signals missed + coaching */}
      {data.buyingSignalsMissed && data.buyingSignalsMissed.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>
            Buying Signals Missed
          </Typography>
          <Stack spacing={1}>
            {data.buyingSignalsMissed.map((bs, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, bgcolor: 'var(--warning-tint)' }}>
                <Typography variant="subtitle2" color="warning.dark">
                  {bs.signal}
                </Typography>
                {bs.repResponse && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Rep said: "{bs.repResponse}"
                  </Typography>
                )}
                {bs.suggestion && (
                  <Typography variant="body2" mt={0.5}>
                    Suggestion: {bs.suggestion}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </SectionShell>
  );
};

export default KbItemProgressionSection;
